/**
 * Back (or reload) to a page: put the section that was on screen back where it
 * was.
 *
 * The browser restores a scroll distance in pixels, and the page it comes back
 * to does not have the heights of the page that was left: sections below the
 * fold are 400px placeholders until they are scrolled to, carousel slides that
 * were never drawn have no height, and some sections only appear once the
 * shopper gets near them. So the same distance points at different content
 * (measured in the app on the home page: several sections past where the
 * shopper had been). Which section was on screen, and where it sat, does not
 * depend on any of those heights.
 *
 * Nothing here names a section or a template. Every section Shopify renders
 * has a stable id, so a section added later is covered without a change here.
 * When a page is one day kept alive on Back instead of reloaded, this script
 * does not run again and has nothing to do.
 */
(() => {
  // Collection and search pages return to a product instead of a section:
  // restoreTappedCard() in assets/results-list.js.
  if (document.querySelector('results-list')) return;

  const state = () => (history.state && typeof history.state === 'object' ? history.state : {});

  // Where the section is kept: session storage, under the key the browser gives
  // the history entry (it survives a reload and a return to the entry). Not
  // history.state: the app's shell takes every history write for a finished page
  // load and runs its page-ready steps again, and on the home page that blanked
  // sections for a frame or two each time scrolling stopped (11 of 12 swipes on
  // video; 0 of 12 without the write). Browsers without the Navigation API have
  // no such key and keep using history.state.
  const STORE = 'includ-section:';
  /** @returns {string | undefined} */
  const entryKey = () => /** @type {any} */ (window).navigation?.currentEntry?.key;
  const load = () => {
    const key = entryKey();
    if (!key) return state().includSection;
    try {
      return JSON.parse(sessionStorage.getItem(STORE + key) ?? 'null');
    } catch {
      return null;
    }
  };
  /** @param {{ id: string, top: number } | null} value */
  const save = (value) => {
    const key = entryKey();
    if (!key) history.replaceState({ ...state(), includSection: value }, '');
    else if (value) sessionStorage.setItem(STORE + key, JSON.stringify(value));
    else sessionStorage.removeItem(STORE + key);
  };
  // Hidden, or present but empty (a section that only shows in the app).
  /** @param {Element} el */
  const hasBox = (el) => el.getBoundingClientRect().height > 0;

  function restore() {
    const saved = load();
    if (!saved || typeof saved.top !== 'number') return;

    // A fresh visit to the address the shopper is already on (the logo on the
    // home page, the same link opened again) replaces the history entry but
    // keeps its key (and, in Chrome, its state). That is not a return: start at
    // the top, as the page did before, and forget the section.
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation instanceof PerformanceNavigationTiming && navigation.type === 'navigate') {
      try {
        save(null);
      } catch {
        // Storage refused; the next record() overwrites it.
      }
      return;
    }

    // For this view of the page only. Left on the history entry, Safari puts the
    // page at the top even when it brings it back whole from its page cache
    // (1,500px -> 0px on a plain page; Chrome keeps the position).
    history.scrollRestoration = 'manual';
    window.addEventListener('pagehide', () => {
      history.scrollRestoration = 'auto';
    });

    const place = () => {
      /** @type {Element | null} */
      let section = document.getElementById(saved.id);
      // A section that is hidden until its content loads (Recently Viewed) has
      // no position yet. Its top is the end of the section before it; once it
      // appears the page grows and this runs again with the real thing.
      let edge = /** @type {'top' | 'bottom'} */ ('top');
      while (section && !hasBox(section)) {
        section = section.previousElementSibling;
        edge = 'bottom';
      }
      if (section) {
        window.scrollBy({ top: Math.round(section.getBoundingClientRect()[edge]) - saved.top, behavior: 'instant' });
      }
    };

    // The section is no longer on the page. The browser may already have applied
    // its own distance; do not leave the shopper at an arbitrary place.
    if (!document.getElementById(saved.id)) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    // Sections draw themselves as they come on screen and change height when
    // they do, which moves everything below them. Keep the section pinned
    // through that until the shopper takes over.
    const pin = new ResizeObserver(place);
    pin.observe(document.body);
    // Each section as well: on a Back load one product list empties while the
    // next one fills in, the page's height does not change, and the section
    // sat 316px off for a second or two until something else resized the page.
    for (const el of document.querySelectorAll('#MainContent > .shopify-section')) pin.observe(el);

    // "Takes over" means scrolls. A tap or a resting thumb while the page is
    // still loading is not that: letting go on the first touch left the page
    // 629px off, because two lists above were drawn after it. `pointerdown` is
    // for a mouse only (a drag of the scrollbar); a finger also fires it on a tap.
    const inputs = ['wheel', 'touchmove', 'keydown', 'pointerdown'];
    /** @param {Event} event */
    const release = (event) => {
      if (event instanceof PointerEvent && event.pointerType !== 'mouse') return;
      pin.disconnect();
      for (const type of inputs) window.removeEventListener(type, release);
    };
    for (const type of inputs) window.addEventListener(type, release, { passive: true });

    place();
  }

  function remember() {
    const record = () => {
      const sections = Array.from(document.querySelectorAll('#MainContent > .shopify-section[id]')).filter(hasBox);
      if (!sections.length) return;

      // The first section that starts on screen; when one section fills the
      // screen, that one; past the last section (the footer), the last one.
      const section =
        sections.find((el) => {
          const { top } = el.getBoundingClientRect();
          return top >= 0 && top < window.innerHeight;
        }) ??
        sections.find((el) => el.getBoundingClientRect().bottom > 0) ??
        sections[sections.length - 1];
      if (!section) return;

      const top = Math.round(section.getBoundingClientRect().top);
      const saved = load();
      if (saved && saved.id === section.id && saved.top === top) return;

      // Never the URL: a change of URL sends a page view to every GA property
      // (measured on the live theme).
      try {
        save({ id: section.id, top });
      } catch {
        // Storage full or refused, or (history.state) Safari's limit of 100
        // history writes in 30 seconds; the next one lands.
      }
    };

    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;
    const soon = () => {
      clearTimeout(timer);
      timer = setTimeout(record, 150);
    };

    // A tab of the app leaves the page without any event, so the position is
    // kept up to date as the shopper goes rather than written on the way out.
    window.addEventListener('scroll', soon, { passive: true });
    // A tap on a link while the page is still moving leaves before the timer
    // fires. Again afterwards, because the tapped control may replace
    // history.state itself (the variant picker does).
    window.addEventListener(
      'click',
      () => {
        record();
        soon();
      },
      true
    );
  }

  restore();
  remember();
})();
